import type { FieldValue } from "@components/job_post/field";
import type { EducationAnswer } from "@components/job_post/education_section";
import type { EmploymentAnswer, EmploymentAnswers } from "@utils/jben/build_application";
import { WEBSITE_FIELD_LABELS } from "@components/job_post/autofill/autofiller";
import type { JobPost, Question } from "@/types/jben/job_post";
import type { WhoamiProfile } from "@/queries/use_whoami";
import { isUploadedFile } from "@utils/uploads/uploaded_file";
import { showEmployment } from "@utils/jben/job_helpers";

type WebsiteCategory = keyof typeof WEBSITE_FIELD_LABELS;

interface QuickApplyEducation {
  school: { label: string } | null;
  degree: { label: string } | null;
  discipline: { label: string } | null;
  startMonth: number | null;
  startYear: number | null;
  endMonth: number | null;
  endYear: number | null;
}

interface QuickApplyEmployment {
  company: string | null;
  title: string | null;
  start_date_month: number | null;
  start_date_year: number | null;
  end_date_month: number | null;
  end_date_year: number | null;
}

export interface QuickApplyProfile {
  user: { first_name: string; last_name: string };
  personal_info: { phone_number: string | null; preferred_name: string | null };
  location: {
    display_name: string;
    latitude: number | null;
    longitude: number | null;
    country_short_name: string | null;
  } | null;
  websites: Record<WebsiteCategory, string | null>;
  educations: QuickApplyEducation[] | null;
  employments: QuickApplyEmployment[];
  self_identification: {
    disability_status: string | null;
    gender: string | null;
    race: string | null;
    veteran_status: string | null;
  };
  resume: { signed_id: string; name: string } | null;
}

function str(value: FieldValue | undefined): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

function num(value: FieldValue | undefined): number | null {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function findQuestionName(questions: Question[], labels: readonly string[]): string | undefined {
  const lowercaseLabels = labels.map((label) => label.toLowerCase());
  const question = questions.find((q) => lowercaseLabels.includes(q.label.toLowerCase()));
  return question?.fields[0]?.name;
}

function collectsEducation(jobPost: JobPost): boolean {
  const config = jobPost.education_config;
  if (!config) return false;
  return Object.values(config).some((value) => value === "optional" || value === "required");
}

function hasEducationData(answer: EducationAnswer): boolean {
  return Boolean(
    answer.school ||
      answer.degree ||
      answer.discipline ||
      answer.start_month ||
      answer.start_year ||
      answer.end_month ||
      answer.end_year,
  );
}

function educationsFromForm(answers: EducationAnswer[]): QuickApplyEducation[] {
  return answers.filter(hasEducationData).map((answer) => ({
    school: answer.school ? { label: answer.school.label } : null,
    degree: answer.degree ? { label: answer.degree.label } : null,
    discipline: answer.discipline ? { label: answer.discipline.label } : null,
    startMonth: (answer.start_month?.value as number) ?? null,
    startYear: answer.start_year ?? null,
    endMonth: (answer.end_month?.value as number) ?? null,
    endYear: answer.end_year ?? null,
  }));
}

function hasEmploymentData(answer: EmploymentAnswer): boolean {
  return Boolean(
    answer.companyName ||
      answer.title ||
      answer.startDateMonth ||
      answer.startDateYear ||
      answer.endDateMonth ||
      answer.endDateYear,
  );
}

function employmentsFromForm(answers: EmploymentAnswers): QuickApplyEmployment[] {
  return answers.filter(hasEmploymentData).map((answer) => ({
    company: answer.companyName ?? null,
    title: answer.title ?? null,
    start_date_month: answer.startDateMonth ?? null,
    start_date_year: answer.startDateYear ?? null,
    end_date_month: answer.currentRole ? null : answer.endDateMonth ?? null,
    end_date_year: answer.currentRole ? null : answer.endDateYear ?? null,
  }));
}

function employmentsFromProfile(profile: WhoamiProfile): QuickApplyEmployment[] {
  return (profile.employments ?? []).map((employment) => ({
    company: employment.company,
    title: employment.title,
    start_date_month: employment.startDate?.month ?? null,
    start_date_year: employment.startDate?.year ?? null,
    end_date_month: employment.endDate?.month ?? null,
    end_date_year: employment.endDate?.year ?? null,
  }));
}

export function buildQuickApplyProfile(
  answers: Record<string, FieldValue>,
  employmentAnswers: EmploymentAnswers,
  educationAnswers: EducationAnswer[],
  questions: Question[],
  jobPost: JobPost,
  profile: WhoamiProfile,
): QuickApplyProfile {
  const websites = {} as Record<WebsiteCategory, string | null>;
  (Object.keys(WEBSITE_FIELD_LABELS) as WebsiteCategory[]).forEach((category) => {
    const questionName = findQuestionName(questions, WEBSITE_FIELD_LABELS[category]);
    const formValue = questionName ? str(answers[questionName]) : undefined;
    websites[category] = formValue ?? profile.websites?.[category] ?? null;
  });

  const formLocationName = str(answers.location);
  const location = formLocationName
    ? {
        display_name: formLocationName,
        latitude: num(answers.latitude),
        longitude: num(answers.longitude),
        country_short_name: str(answers.country_short_name) ?? null,
      }
    : profile.location
      ? {
          display_name: profile.location.displayName,
          latitude: profile.location.latitude,
          longitude: profile.location.longitude,
          country_short_name: profile.location.countryShortName,
        }
      : null;

  const formEducations = collectsEducation(jobPost) ? educationsFromForm(educationAnswers) : [];
  const educations = formEducations.length > 0 ? formEducations : null;

  const formEmployments = showEmployment(jobPost) ? employmentsFromForm(employmentAnswers) : [];
  const employments =
    formEmployments.length > 0 ? formEmployments : employmentsFromProfile(profile);

  const hasEeoc = (jobPost.eeoc_sections?.length ?? 0) > 0;
  const selfIdentification = profile.selfIdentification;
  const eeocValue = (key: string, profileValue: number | null | undefined): string | null => {
    const formValue = hasEeoc ? str(answers[key]) : undefined;
    return formValue ?? (profileValue != null ? profileValue.toString() : null);
  };

  const formResume = answers.resume;
  // signedId is set only for a file uploaded this session — a resume prefilled
  // from the saved profile has none — so this naturally skips redundant
  // re-saves and avoids ever trusting a client-supplied URL server-side.
  const resume =
    isUploadedFile(formResume) && formResume.signedId
      ? { signed_id: formResume.signedId, name: formResume.name }
      : null;

  return {
    user: {
      first_name: str(answers.first_name) ?? profile.firstName ?? "",
      last_name: str(answers.last_name) ?? profile.lastName ?? "",
    },
    personal_info: {
      phone_number: str(answers.phone) ?? profile.phoneNumber ?? null,
      preferred_name: str(answers.preferred_name) ?? profile.preferredName ?? null,
    },
    location,
    websites,
    educations,
    employments,
    self_identification: {
      disability_status: eeocValue("disability_status", selfIdentification?.disabilityStatus),
      gender: eeocValue("gender", selfIdentification?.gender),
      race: eeocValue("race", selfIdentification?.race),
      veteran_status: eeocValue("veteran_status", selfIdentification?.veteranStatus),
    },
    resume,
  };
}
